import { refuseRidedState } from "@/utils/ridedStateGuard";
import moment from "moment";
import {
  getStateColor,
  getStateIcon,
  checkLateDate,
  formatDateBookingDates,
  formatTimeBookingDates,
} from "@/components/dashboard/bookings/bookingFormatters";
import { bookingPaymentChip } from "@/components/dashboard/bookings/paymentStatus";

export default {
  props: {
    booking: { type: Object, required: true },
    states: { type: Array, default: () => [] },
  },
  data() {
    return {
      acting: false,
      channelDialog: false,
      channelMode: "confirm",
      channels: { email: true, whatsapp: true, sms: false },
      editingDesc: false,
      descDraft: "",
      savingDesc: false,
    };
  },
  computed: {
    company() {
      return this.$store.getters.getCompany && this.$store.getters.getCompany.company;
    },
    // Паритет с теми же операциями в списке броней: canAct открывает смену
    // статуса и правку описания карточки — это BookingsListView#saveBookingState
    // и #saveDescriptionEdit, они гейтятся правом компании, а не ролью.
    // Прежняя форма «не guest и не partner» пускала агента без этого права.
    canAct() {
      return this.isStaff || this.agentCan("can_edit_booking");
    },
    canNotify() {
      return (
        !this.booking.technical &&
        !this.booking.vseprokaty_id &&
        !this.booking.localrent_id &&
        this.booking.source !== "Localrent"
      );
    },
    // Confirm/decline/template only make sense for pre-rental (backlog) bookings —
    // not for active (waiting/in-rent) or finished (rided) ones.
    isBacklog() {
      return !this.booking.active && !this.booking.ride;
    },
    stateColor() {
      return getStateColor(this.booking.state);
    },
    stateIcon() {
      return getStateIcon(this.booking.state);
    },
    lateStyle() {
      // rided bookings are already finished — a past end_date isn't "overdue", so no
      // red highlight in the "Отъездила" column (it only distracts).
      if (this.booking.ride) return "";
      return checkLateDate(this.booking.end_date);
    },
    // Чип оплаты — общий хелпер с списком (/bookings/list): подпись с валютой
    // компании («To pay 417 ₽» / «Overpaid 50 ₽» / «Paid»), цвет по правилу
    // selectColorPaid: зелёный = оплачено, amber = переплата или ещё-не-должник,
    // красный = должник (аренда идёт/завершена, не техническая). null — чипа нет.
    paymentChip() {
      return bookingPaymentChip(this.booking, {
        t: (key, params) => this.$t(key, params),
        currency: (this.company && this.company.currency) || "",
        multicurrency: Boolean(this.company && this.company.multicurrency_cashboxes == true),
      });
    },
    tasksOverdue() {
      return (this.booking.tasks_preview || []).some((t) => t.overdue);
    },
  },
  methods: {
    formatDate: formatDateBookingDates,
    formatTime: formatTimeBookingDates,
    getColor: getStateColor,
    getIcon: getStateIcon,
    formatDue(d) {
      return moment(d).format("DD MMM");
    },
    authHeaders() {
      return { headers: { Authorization: this.$store.getters.getAuthToken } };
    },
    openBooking() {
      this.$router.push({ name: "BookingCard", params: { id: this.booking.id } });
    },
    startEditDesc() {
      this.descDraft = this.booking.description || "";
      this.editingDesc = true;
    },
    cancelDesc() {
      this.editingDesc = false;
    },
    saveDesc() {
      if (!this.editingDesc || this.savingDesc) return;
      const value = this.descDraft;
      if (value === (this.booking.description || "")) {
        this.editingDesc = false;
        return;
      }
      this.savingDesc = true;
      this.axios
        .patch(`/api/v1/bookings/${this.booking.id}`, { bookings: { description: value } }, this.authHeaders())
        .then(() => {
          // Optimistic local update on the shared booking object (same object the
          // parent column array holds) — no board reload needed for a note change.
          // eslint-disable-next-line vue/no-mutating-props
          this.booking.description = value;
          this.editingDesc = false;
        })
        .catch((error) => this.setError && this.setError(error, this.$t("bookings.booking_desc")))
        .finally(() => (this.savingDesc = false));
    },
    changeStatus(state) {
      if (refuseRidedState(this, this.booking, state)) return;
      if (state === "Активная" || state === "Отъездила") {
        this.$notify(
          this.$t("errors.warning_title"),
          this.$t("bookings_states.select_state_warning_active_rided"),
          "warning",
          3000
        );
      }
      this.acting = true;
      this.axios
        .patch(`/api/v1/bookings/${this.booking.id}`, { bookings: { state } }, this.authHeaders())
        .then(() => this.$emit("changed"))
        .catch((error) => this.setError && this.setError(error, this.$t("errors.update_state")))
        .finally(() => (this.acting = false));
    },
    archive() {
      // FT-082: one-click archive (no confirm dialog) — the card leaving its column is
      // the feedback, and archived bookings can be restored from the archive page.
      // Архивация — только сотруднику: не-сотруднику сервер ответит 403.
      if (this.acting || !this.isStaff) return;
      this.acting = true;
      this.axios
        .patch(`/api/v1/bookings/${this.booking.id}`, { bookings: { archive: true } }, this.authHeaders())
        // Оптимистичное удаление: заархивированная бронь исключена из всех колонок,
        // поэтому убираем карточку локально (см. BookingsBoard#onCardArchived) без
        // полного рефетча доски — поштучная архивация «Отъездила» больше не порождает
        // лавину POST /bookings/board.
        .then(() => this.$emit("archived", this.booking.id))
        .catch((error) => this.setError && this.setError(error, this.$t("errors.bad_archivate")))
        .finally(() => (this.acting = false));
    },
    openChannels(mode) {
      this.channelMode = mode;
      this.channels = { email: true, whatsapp: true, sms: false };
      this.channelDialog = true;
    },
    sendChannels() {
      const c = this.channels;
      const base =
        this.channelMode === "confirm"
          ? `/api/v1/accept_booking/${this.booking.id}`
          : `/api/v1/cancel_booking_not_cars/${this.booking.id}`;
      const url = `${base}?email=${c.email}&whatsapp=${c.whatsapp}&sms=${c.sms}`;
      this.acting = true;
      this.axios
        .get(url, this.authHeaders())
        .then(() => {
          this.$notify(this.$t("bookings.send_success"), "", "success", 3000);
          this.channelDialog = false;
          this.$emit("changed");
        })
        .catch((error) => this.setError && this.setError(error, this.$t("errors.bad_message_title")))
        .finally(() => (this.acting = false));
    },
  },
};
